import React, { useState, useEffect } from "react";
import i18n from "i18next";

// Fluent UI Imports
import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { baseUrl } from "../../../helpers/constants";
import { ILoggingEntriesDto } from "../../../Models/ILoggingEntriesDto";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  Option,
  Dropdown,
  Button,
  Field,
  Input,
  Switch,
  ProgressBar,
} from "@fluentui/react-components";
import { FolderSearchRegular, SearchRegular } from "@fluentui/react-icons";
import LuebPagedDataGrid from "../../../Controls/LuebPagedDataGrid";
import { LoggingColumns } from "./LoggingColumns";
import Header from "../../Layout/Header";

function Uploader(props: any) {
  const [methodOptions, setMethodOptions] = useState<
    { key: string; text: string }[]
  >([]);
  const [logEntries, setLogEntries] = useState<
    ILoggingEntriesDto | undefined
  >();
  const [page, setPage] = useState<number>(1);

  const [autoUpdate, setAutoUpdate] = useState(true);
  const [counter, setCounter] = React.useState(20);

  const [queryValues, setQueryValues] = useState({
    filter: "all",
    reference: "",
    message: "",
    method: "all",
  });
  function handleQueryValuesChange(name: string, val: any) {
    setQueryValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
  }

  const filterOptions: { key: string; text: string }[] = [
    {
      key: "all",
      text: "Alle",
    },
    {
      key: "error",
      text: "Fehler",
    },
    {
      key: "warning",
      text: "Warnung",
    },
    {
      key: "info",
      text: "Info",
    },
  ];

  let iconName = "Filter";
  switch (queryValues.filter) {
    case "all":
      iconName = "Filter";
      break;
    case "error":
      iconName = "ErrorBadge";
      break;
    case "info":
      iconName = "Info";
      break;
    case "warning":
      iconName = "Warning";
      break;
    default:
      iconName = "Filter";
  }

  // Functions
  const GetLogEntries = () => {
    fetchWrapper
      .get(
        `${baseUrl}/api/Logging/getbyfilter?filter=${
          queryValues.filter == "all" ? "" : queryValues.filter
        }&reference=${queryValues.reference}&message=${
          queryValues.message
        }&method=${
          queryValues.method == "all" ? "" : queryValues.method
        }&page=${page}`,
      )
      .then((result: ILoggingEntriesDto) => {
        setLogEntries(result);
      });
  };

  function getMethods() {
    // Clear Log
    fetchWrapper.get(`${baseUrl}/api/logging/getmethods`).then((result) => {
      var newMethodOptions: { key: string; text: string }[] = result.map(
        (x: string) => {
          const method: { key: string; text: string } = { key: x, text: x };
          return method;
        },
      );
      newMethodOptions.splice(0, 0, { key: "all", text: "Alle" });
      setMethodOptions(newMethodOptions);
    });
  }

  useEffect(() => {
    GetLogEntries();
    getMethods();
  }, []);

  useEffect(() => {
    // Alle 60 Sekunden das Log und die UploadJobs erneuern
    const interval = setInterval(() => {
      if (autoUpdate) {
        setCounter(counter == 0 ? 20 : counter - 1);
        if (counter == 0) {
          GetLogEntries();
          getMethods();
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [autoUpdate, counter]);

  // Rendering
  return (
    <div>
      <Header
        HTMLElement={
          <StackShim horizontal verticalAlign="center">
            <Button
              icon={<FolderSearchRegular fontSize={24} color="#ff6600" />}
              size="large"
              appearance="subtle"
            >
              LOGGING
            </Button>
          </StackShim>
        }
      />

      <div className="lueb-InnerContentBlockPadding2px">
        <Button
          onClick={() => {
            GetLogEntries();
            setCounter(60);
          }}
          icon={<SearchRegular />}
          appearance="transparent"
        >
          {i18n.t<string>("search")}
        </Button>
        <Switch
          onChange={(ev, data) => {
            setAutoUpdate(data.checked);
          }}
          checked={autoUpdate}
          labelPosition="after"
          label={
            <div style={{ display: "flex", flexDirection: "row" }}>
              {"Auto-Update: "}
              {autoUpdate == true && "Ein"}
              {autoUpdate == false && "Aus"}
            </div>
          }
        ></Switch>
      </div>

      <Field
        validationMessage={
          autoUpdate == true
            ? "Update in " + counter + " Sekunden"
            : "Auto-Update ist aus"
        }
        validationState="none"
      >
        <ProgressBar
          thickness="large"
          value={counter * 0.05 > 1 ? counter * 0.016666 : counter * 0.05}
        />
      </Field>
      <div className="lueb-ContentBlock">
        <StackShim horizontal tokens={{ childrenGap: 5 }} wrap={true}>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            <Field label="Typ">
              <Dropdown
                value={
                  filterOptions.find((x) => x.key == queryValues.filter)?.text
                }
                selectedOptions={[queryValues.filter]}
                onOptionSelect={(ev, val) => {
                  handleQueryValuesChange("filter", val ? val.optionValue : "");
                  GetLogEntries();
                }}
              >
                {filterOptions.map((item) => {
                  return <Option value={item.key}>{item.text}</Option>;
                })}
              </Dropdown>
            </Field>
          </StackItemShim>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            {methodOptions.find((x) => x.key == queryValues.method)?.text && (
              <Field label="Methode">
                <Dropdown
                  value={
                    methodOptions.find((x) => x.key == queryValues.method)?.text
                  }
                  selectedOptions={[queryValues.method]}
                  onOptionSelect={(ev, val) => {
                    handleQueryValuesChange(
                      "method",
                      val ? val.optionValue : "",
                    );
                    GetLogEntries();
                  }}
                >
                  {methodOptions.map((item) => {
                    return <Option value={item.key}>{item.text}</Option>;
                  })}
                </Dropdown>
              </Field>
            )}
          </StackItemShim>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            <Field label="Nachricht">
              <Input
                type="search"
                placeholder="Nachricht"
                onChange={(ev, val) =>
                  handleQueryValuesChange("message", val ? val.value : "")
                }
              />
            </Field>
          </StackItemShim>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            <Field label="Referenz">
              <Input
                type="search"
                placeholder="Referenz"
                onChange={(ev, val) => {
                  handleQueryValuesChange("reference", val ? val.value : "");
                }}
              />
            </Field>
          </StackItemShim>
        </StackShim>
      </div>
      <div className="lueb-ContentBlock">
        <LuebPagedDataGrid
          sortable
          columns={LoggingColumns({})}
          items={logEntries ? logEntries?.entries : []}
          resizableColumns={true}
          columnSizingOptions={{
            timeStamp: { defaultWidth: 150, minWidth: 150 },
            reference: { defaultWidth: 150, minWidth: 150 },
            message: { defaultWidth: 600, minWidth: 250 },
            type: { defaultWidth: 50, minWidth: 30 },
          }}
        />
      </div>
    </div>
  );
}
export default Uploader;
